import type { Location } from "@swift/shared";
import { IconLocation } from "./icons";

/**
 * Compacte locatie-kaart die opent in de standaard kaart-app van het
 * apparaat. Bewust GEEN automatische tile-fetch van tile.openstreetmap.org
 * meer: dat zou bij elk inkomend locatie-bericht het IP + globale ligging
 * van élke ontvanger naar OSM's CDN sturen — contrair aan de "data
 * verlaat nooit de eigen VPS"-policy. Klik op de kaart opent de externe
 * app pas op dat moment, en dan op uitdrukkelijke handeling van de
 * gebruiker. Latere iteratie kan een server-side tile-proxy toevoegen
 * voor anonieme previews.
 */
export default function LocationCard({ location }: { location: Location }) {
  const { lat, lng, label } = location;
  // Externe-app URL. Android opent `geo:` met de gekozen kaart-app
  // (Maps, OsmAnd, etc.); andere platforms vallen terug op OSM in
  // de browser via de onClick-fallback.
  const geoUrl = `geo:${lat},${lng}?q=${lat},${lng}${
    label ? `(${encodeURIComponent(label)})` : ""
  }`;
  const osmUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=15/${lat}/${lng}`;

  return (
    <a
      href={geoUrl}
      onClick={(e) => {
        // Op desktop bestaat `geo:` niet — open dan OSM in 'n nieuw
        // venster. Op mobiel laat de OS de geo:-handler intercepten.
        if (typeof window !== "undefined" && !/Android|iPhone|iPad/i.test(navigator.userAgent)) {
          e.preventDefault();
          window.open(osmUrl, "_blank", "noopener");
        }
      }}
      className="mt-2 flex max-w-xs items-center gap-3 rounded-md border border-slate-700 bg-slate-800 px-3 py-2 hover:border-slate-500"
      title={label ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-700 text-slate-300" aria-hidden="true">
        <IconLocation size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-100">
          {label ?? "Locatie"}
        </div>
        <div className="truncate text-xs text-slate-400">
          {lat.toFixed(5)}, {lng.toFixed(5)}
        </div>
        <div className="text-xs text-accent">Open in kaart →</div>
      </div>
    </a>
  );
}
